import cloneDeep from "clone-deep";

export type OptimisticActionSet<T> = {
  handler: (model: Ref<T>, ...args: any[]) => Promise<any>;
  optimisticUpdate: T | ((model: Ref<T>, initial: T) => T);
  onError: T | ((err: Error, model: Ref<T>, initial: T) => T);
};

export type HandlerWithArgsOnly<Handler> = Handler extends (
  model: Ref<any>,
  ...args: (infer U)[]
) => Promise<infer V>
  ? (...args: U[]) => Promise<V>
  : never;

export type OptimisticActions<T> = Record<string, OptimisticActionSet<T>>;

/**
 * A composable to perform optimistic UI updates.
 */
export function useOptimistic<
  T,
  A extends Record<string, OptimisticActionSet<T>>,
>(
  model: MaybeRef<T>,
  actions: A,
): {
  model: Ref<T>;
  actions: { [K in keyof A]: HandlerWithArgsOnly<A[K]["handler"]> };
} {
  const modelRef = ref(model) as Ref<T>;

  function updateModel(valueOrFunction: T | Function, ...args: any[]) {
    if (typeof valueOrFunction === "function") {
      modelRef.value = (valueOrFunction as Function)(...args);
    } else {
      modelRef.value = valueOrFunction;
    }
  }

  function wrapAction(
    name: string,
    { handler, optimisticUpdate, onError }: OptimisticActionSet<T>,
  ) {
    const wrappedFunction = async (...args: any[]) => {
      const initial = cloneDeep(modelRef.value);

      try {
        updateModel(optimisticUpdate, modelRef, initial);
        return await handler(modelRef, ...args);
      } catch (e: any) {
        updateModel(onError, e, modelRef, initial);
      }
    };

    return [name, wrappedFunction];
  }

  return {
    model: modelRef,
    actions: Object.fromEntries(
      Object.entries(actions).map(([name, actionSet]) =>
        wrapAction(name, actionSet),
      ),
    ),
  };
}

/**
 * A composable to perform optimistic UI updates with initial state that's resolved asynchronously.
 */
export function useOptimisticAsync<T, A extends OptimisticActions<T>>(
  fetcher: () => Promise<T>,
  actions: A,
  options: {
    initialValue: T;
    immediate?: boolean;
  },
): {
  model: Ref<T>;
  pending: Ref<boolean>;
  error: Ref<Error | null>;
  refresh: () => Promise<void>;
  actions: { [K in keyof A]: HandlerWithArgsOnly<A[K]["handler"]> };
} {
  const modelRef = ref(options.initialValue) as Ref<T>;

  function updateModel(valueOrFunction: T | Function, ...args: any[]) {
    if (typeof valueOrFunction === "function")
      modelRef.value = (valueOrFunction as Function)(...args);
    else modelRef.value = valueOrFunction;
  }

  function wrapAction({
    handler,
    optimisticUpdate,
    onError,
  }: OptimisticActionSet<T>) {
    const wrappedFunction = async (...args: any[]) => {
      const initial = cloneDeep(modelRef.value);

      try {
        updateModel(optimisticUpdate, modelRef, initial);
        return await handler(modelRef, ...args);
      } catch (e: any) {
        updateModel(onError, e, modelRef, initial);
      }
    };

    return wrappedFunction;
  }

  const { data, error, pending, refresh } = useAsyncData(fetcher, {
    server: false,
    immediate: options.immediate,
  });

  watch(data, (v) => (modelRef.value = (v as T) ?? options.initialValue));

  return {
    model: modelRef,
    error: error,
    pending: pending,
    actions: Object.fromEntries(
      Object.entries(actions).map(([name, actionSet]) => [
        name,
        wrapAction(actionSet),
      ]),
    ) as { [K in keyof A]: HandlerWithArgsOnly<A[K]["handler"]> },
    refresh: async () => {
      refresh();
    },
  };
}
